<template>
  <div class="instagram-feed p-6">
    <div class="flex justify-between items-center mb-6">
      <h2 class="text-xl font-semibold">Instagram Posts</h2>
      <Button variant="outline" size="sm" :disabled="isSharingSet" @click="handleShareSet">
        <Icon v-if="!isSharingSet" name="heroicons:share" class="h-4 w-4 mr-1.5" />
        <Icon v-else name="heroicons:arrow-path" class="h-4 w-4 mr-1.5 animate-spin" />
        {{ isSharingSet ? 'Creating Link...' : 'Share Post Set' }}
      </Button>
    </div>

    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
      <AppLink v-for="post in sortedPosts" :key="post.uuid" :to="`/bubble/${post.uuid}`">
        <div
          class="post border rounded-lg overflow-hidden hover:shadow-md transition-shadow bg-card"
        >
          <!-- Post Image -->
          <div v-if="post.offline" class="relative w-full">
            <AspectRatio :ratio="1">
              <NuxtImg
                :src="getcover_photoSrc(post as InstagramBubble)"
                :alt="(post as InstagramBubble).author?.[0] || 'Unknown Author'"
                class="w-full h-full object-cover"
                loading="lazy"
              />
            </AspectRatio>
          </div>

          <!-- Post Content -->
          <div class="p-4">
            <div class="flex items-center mb-3">
              <template v-if="getAuthorPicSrc(post as InstagramBubble)">
                <NuxtImg
                  :src="getAuthorPicSrc(post as InstagramBubble)"
                  :alt="(post as InstagramBubble).author?.[0] || 'Unknown Author'"
                  class="w-8 h-8 rounded-full mr-2"
                  loading="lazy"
                />
              </template>
              <Icon
                v-else
                name="heroicons:user-circle"
                class="w-8 h-8 text-muted-foreground mr-2"
              />
              <div>
                <h3 class="font-medium text-sm">
                  {{ (post as InstagramBubble).author?.[0] || 'Unknown Author' }}
                </h3>
                <p class="text-xs text-muted-foreground">
                  @{{ (post as InstagramBubble).authorHandle || 'unknown' }}
                </p>
              </div>
            </div>
            <p class="text-foreground text-sm whitespace-pre-wrap line-clamp-3">
              {{ (post as InstagramBubble).caption }}
            </p>
          </div>
        </div>
      </AppLink>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="text-center py-8">
      <p class="text-muted-foreground">Loading posts...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-8 text-destructive">
      <p>{{ error }}</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="instagramBubbles.length === 0" class="text-center py-8">
      <p class="text-muted-foreground mb-4">You don't have any Instagram posts yet.</p>
      <Button @click="router.push('/dashboard/add')">Add Your First Post</Button>
    </div>

    <!-- Load More Button -->
    <div v-if="canLoadMore" class="mt-6 text-center">
      <Button variant="default" :disabled="isLoading" class="hover:opacity-90" @click="loadMore">
        Load More Posts
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, h } from 'vue'
import { useBubblesStore } from '~/stores/bubbles'
import { useUserShareStore } from '~/stores/userShare'
import type { InstagramBubble } from '~/stores/interfaces'
import { useRuntimeConfig } from '#app'
import { useRouter } from 'vue-router'
import { useNavigationStore } from '@/stores/navigationStore'
import { AspectRatio } from '@/components/ui/aspect-ratio'
import { Button } from '@/components/ui/button'
import { useToast } from '@/components/ui/toast'
import CopyButton from '@/components/ui/button/CopyButton.vue'
import AppLink from '@/components/ui/link/AppLink.vue'
import { useClipboard } from '@vueuse/core'

const bubblesStore = useBubblesStore()
const userShareStore = useUserShareStore()
const { toast } = useToast()
const { copy, copied, isSupported } = useClipboard()
const {
  public: { dataStore },
} = useRuntimeConfig()
const router = useRouter()
const navigationStore = useNavigationStore()

const POSTS_PER_PAGE = 12
const page = ref(1)
const isLoading = ref(false)
const error = ref<string | null>(null)
const isSharingSet = ref(false)

const instagramBubbles = computed(
  () => (bubblesStore.bubblesByType['insta'] || []) as InstagramBubble[]
)

const sortedPosts = computed(() => {
  const sorted = [...instagramBubbles.value]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, page.value * POSTS_PER_PAGE)

  navigationStore.setCurrentType('insta')
  navigationStore.setCurrentTypeItems(sorted.map((post) => post.uuid))

  return sorted
})

const canLoadMore = computed(() => {
  return instagramBubbles.value.length > page.value * POSTS_PER_PAGE
})

onMounted(async () => {
  if (
    !bubblesStore.bubblesByType['insta'] ||
    bubblesStore.bubblesByType['insta'].length < POSTS_PER_PAGE
  ) {
    try {
      isLoading.value = true
      error.value = null
      await bubblesStore.fetchBubblesByType('insta')
    } catch (err) {
      error.value = 'Failed to load Instagram posts. Please try again later.'
      logError('Failed to fetch Instagram posts:', err)
    } finally {
      isLoading.value = false
    }
  }
})

async function loadMore() {
  page.value++
  if (page.value * POSTS_PER_PAGE >= instagramBubbles.value.length) {
    try {
      isLoading.value = true
      error.value = null
      await bubblesStore.fetchBubblesByType('insta')
    } catch (err) {
      error.value = 'Failed to load more Instagram posts. Please try again later.'
      logError('Failed to fetch Instagram posts:', err)
    } finally {
      isLoading.value = false
    }
  }
}

function getAuthorPicSrc(bubble: InstagramBubble): string | undefined {
  if (bubble.offline && bubble.offlineMediaResources?.author_pic) {
    return `${dataStore}${bubble.offlineMediaResources.author_pic}`
  }
  // return bubble.authorPic || undefined
  return undefined
}

function getcover_photoSrc(bubble: InstagramBubble): string | undefined {
  if (bubble.offline && bubble.offlineMediaResources?.cover_photo) {
    return `${dataStore}${bubble.offlineMediaResources.cover_photo}`
  }
  // return bubble.coverPhoto || undefined
  return undefined
}

const handleShareSet = async () => {
  isSharingSet.value = true
  try {
    const shareData = {
      shareType: 'set' as const,
      targetBubbleType: 'insta',
      isPublic: true,
    }
    const result = await userShareStore.createShareLink(shareData)

    if (result && result.url) {
      toast({
        title: 'Share Link Created',
        description: 'Your set of Instagram posts can be shared via this link.',
        duration: 9000,
        action: h(CopyButton, { textToCopy: result.url, variant: 'secondary' }),
      })
      if (isSupported.value) {
        await copy(result.url)
        if (copied.value) {
          toast({
            title: 'Link Copied!',
            description: 'The share link has been copied to your clipboard.',
            duration: 3000,
          })
        } else {
          toast({
            title: 'Copy Failed',
            description:
              'Could not automatically copy the link. Please use the button in the previous message.',
            variant: 'destructive',
            duration: 5000,
          })
        }
      }
    } else {
      if (!result) {
        toast({
          title: 'Error',
          description: 'Could not create share link for set.',
          variant: 'destructive',
        })
      }
      // Store handles other errors
    }
  } catch (error) {
    logError('Error creating Instagram set share link:', error)
    toast({
      title: 'Error',
      description: 'An unexpected error occurred while sharing the set.',
      variant: 'destructive',
    })
  } finally {
    isSharingSet.value = false
  }
}
</script>

<style scoped>
.instagram-feed {
  max-width: 1200px;
  margin: 0 auto;
}

.post {
  break-inside: avoid;
  display: flex;
  flex-direction: column;
}

.post:hover img {
  transform: scale(1.02);
  transition: transform 0.3s ease;
}
</style>
